import BlockedAssignmentQuestion from 'client/components/QuestionTypes/BlockedAssignmentQuestion/BlockedAssignmentQuestion';
import {
  BlockedForStudentQuestion,
  isQuestionRestrictedForStudent
} from 'client/components/ContentDiscovery/StudentRestrictions';
import {getRendererForQuestion} from 'components/QuestionTypes/getRendererForQuestion';
import React from 'react';
import {
  BlockedForTeacherQuestion,
  isQuestionRestrictedForTeacher
} from 'client/components/TeacherRestrictedContent';
import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';
// import {Store} from 'client/framework'; TODO-KBC: Build typescript methods

interface Props {
  actions?: React.ReactNode;
  activeGuess?: any;
  activeQuestion: any;
  activeQuestionSet?: any;
  activeQuestionStore?: any; // PropTypes.instanceOf(Store) TODO-KBC: Build typescript methods
  activeSubject?: any;
  answerInputPaneRef?: React.RefObject<HTMLDivElement | null>;
  customHeader?: any;
  displayOrientation?: 'default' | 'vertical' | 'horizontal';
  hasHeading?: boolean;
  headingActions?: React.ReactNode;
  /** Rendered as the first child of the question heading row, before the title. */
  leadingHeaderAction?: React.ReactNode;
  /** Keep the question title on one line, ellipsised, instead of wrapping. */
  truncateTitle?: boolean;
  interactive?: boolean;
  isMobile?: boolean;
  modifyTitle?: () => {};
  navigateBackProps?: {[key: string]: any} | null;
  navigateNextProps?: {[key: string]: any} | null;
  onResize?: (resizerPosition: number) => void;
  postSubmissionScrollPointRef?: {[key: string]: any};
  rendererType?: 'PreGuess' | 'PostGuess' | 'Dennis';
  resizerPosition?: number;
  showDifficulty?: boolean;
  stackedContainerRef?: React.RefObject<HTMLDivElement | null>;
  hasQuestionSetAccessPredicate?: (questionSet: any) => boolean;
  partialCreditEnabled?: boolean;
  shouldSetContentMaxWidth?: boolean;
  useAdaptiveResizer?: boolean;
  shouldShowCopyright?: boolean;
  collapseSampleResponse?: boolean;
  /** Set where the response belongs to someone else — a teacher reading a student's submission. */
  isViewingStudentWork?: boolean;
}

const QuestionRenderer = ({
  actions,
  activeGuess,
  activeQuestion,
  activeQuestionSet,
  activeQuestionStore,
  activeSubject,
  answerInputPaneRef,
  customHeader,
  displayOrientation = 'default',
  hasHeading = true,
  headingActions,
  leadingHeaderAction,
  truncateTitle,
  interactive,
  isMobile,
  modifyTitle,
  navigateBackProps,
  navigateNextProps,
  onResize,
  postSubmissionScrollPointRef,
  rendererType,
  resizerPosition,
  showDifficulty,
  stackedContainerRef,
  hasQuestionSetAccessPredicate,
  partialCreditEnabled = false,
  shouldSetContentMaxWidth,
  useAdaptiveResizer,
  shouldShowCopyright,
  collapseSampleResponse,
  isViewingStudentWork
}: Props) => {
  // Both predicates resolve through sessionStore. sessionStore is only the subscription anchor —
  // useFluxStore subscribes to the whole state tree, so the selector may close over activeQuestion.
  const isRestrictedForTeacher = useFluxStore(sessionStore, () =>
    isQuestionRestrictedForTeacher(activeQuestion)
  );
  const isRestrictedForStudent = useFluxStore(sessionStore, () =>
    isQuestionRestrictedForStudent(activeQuestion)
  );

  // Restricted content (admin-locked-assessments) is backend-redacted, so the block panels take
  // precedence over the normal renderer. The teacher block (Parts 2/3) is checked first; the student
  // block (Part 4, #17843) replaces the whole renderer for a student viewing a restricted question
  // inside an unauthorized assignment — no stem, answer input, explanation, or graded state. Both
  // gate synchronously on the server-set is_content_locked signal (only emitted under the
  // global_content_restriction_enforcement flag) so the block engages on the FIRST render; the
  // backend nulls the content, so any async gate would let the empty question render/crash first.
  let Renderer: React.ElementType;
  if (isRestrictedForTeacher) {
    Renderer = BlockedForTeacherQuestion;
  } else if (activeQuestion.getMeta().isBlockedAssignmentQuestion?.()) {
    Renderer = BlockedAssignmentQuestion;
  } else if (isRestrictedForStudent) {
    Renderer = BlockedForStudentQuestion;
  } else {
    Renderer = getRendererForQuestion(activeQuestion)[rendererType];
  }
  return (
    <Renderer
      actions={actions}
      activeQuestion={activeQuestion}
      activeQuestionSet={activeQuestionSet}
      activeQuestionStore={activeQuestionStore}
      answerInputPaneRef={answerInputPaneRef}
      resizerPosition={resizerPosition}
      displayOrientation={displayOrientation}
      isMobile={isMobile}
      onResize={onResize}
      guess={activeGuess}
      activeSubject={activeSubject || null}
      showDifficulty={showDifficulty}
      customHeader={customHeader || null}
      hasHeading={hasHeading}
      headingActions={headingActions}
      leadingHeaderAction={leadingHeaderAction}
      truncateTitle={truncateTitle}
      interactive={interactive}
      modifyTitle={modifyTitle}
      navigateBackProps={navigateBackProps}
      navigateNextProps={navigateNextProps}
      postSubmissionScrollPointRef={postSubmissionScrollPointRef}
      stackedContainerRef={stackedContainerRef}
      hasQuestionSetAccessPredicate={hasQuestionSetAccessPredicate}
      partialCreditEnabled={partialCreditEnabled}
      shouldSetContentMaxWidth={shouldSetContentMaxWidth}
      useAdaptiveResizer={useAdaptiveResizer}
      shouldShowCopyright={shouldShowCopyright}
      collapseSampleResponse={collapseSampleResponse}
      isViewingStudentWork={isViewingStudentWork}
    />
  );
};

export default QuestionRenderer;
